import {
  Component,
  DoCheck,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import {
  DropDownOptions,
  ExtendedGLAccount,
  IBudgetAllocForm,
  MyPurchaseForm,
  StoreDropdowns,
  UserInformation,
} from 'src/app/shared/models/app-models';
import { SelectOption } from '../item-budget.component';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject, lastValueFrom, takeUntil } from 'rxjs';
import { BudgetForm } from '../../add-item-page.component';
import { isEqual } from 'lodash';
import { BudgetTableAction } from '../budget-listing-table/budget-listing-table.component';
import { SAPAPI } from 'src/app/core/services/sap-api';
import { PROJECT_COMPLETION_MESSAGE } from 'src/app/modules/shared/constants';
import { BackendApiService } from 'src/app/core/services/backend-api';
import { BudgetTypeDropDownOptions } from 'src/app/shared/models/app-models/DropDownOption.model';
import { AlertType } from 'src/app/shared/components/alert/alert.component';

@Component({
  selector: 'app-add-budget',
  templateUrl: './add-budget.component.html',
  styleUrls: ['./add-budget.component.scss'],
})
export class AddBudgetComponent implements OnInit, OnDestroy, OnChanges {
  @Input() isReadOnly: boolean = false;
  @Input() control!: FormGroup;
  @Input() budgetInitialState!: IBudgetAllocForm;
  @Input() formType: string = 'single';
  @Input() isQuantity: boolean = true;
  @Input() BudgetForm!: BudgetForm;
  @Input() ActionType: string = 'Add';
  @Output() addBudgetHandler = new EventEmitter();
  @Output() budgetTypeValue = new EventEmitter();
  @Output() showProjectCompletionMessage = new EventEmitter();
  userInformation?: UserInformation
  currenBudgetList: IBudgetAllocForm[] = [];
  shouldShowMultiBudgetFormProjectCompletionMessage: boolean = false
  projectCompletionMessage = PROJECT_COMPLETION_MESSAGE
  destroy$: Subject<boolean> = new Subject<boolean>();
  // dropdowns
  budgetTypedropDownOptions: BudgetTypeDropDownOptions[] = [];
  costCentreDropDownOptions: DropDownOptions[] = [];
  fundCentreDropDownOptions: DropDownOptions[] = [];
  projectBudgetDropDownOptions: any[] = [];
  allowableProjectBudgetDropDownOptions: any[] = [];
  originalProjectCodeDropDownOptions: DropDownOptions[] = [];
  projectCodeDropDownOptions: DropDownOptions[] = [];
  glAccountDropDownOptions: ExtendedGLAccount[] = [];
  orderNumberDropDownOptions: DropDownOptions[] = [];
  originalProjectBudgetDropDownOptions: any[] = [];
  originalGLAccountDropDownOptions: ExtendedGLAccount[] = [];

  // select options
  budgetExpenditureOptions: SelectOption[] = [];
  budgetFrequencyOptions: SelectOption[] = [];

  // form controls
  budgetTypeControl!: FormControl;
  budgetExpenditureControl!: FormControl;
  budgetFrequencyControl!: FormControl;
  projectCodeControl!: FormControl;

  mode: string = 'add';
  itemNo: number = 1;
  isSingleBudget: boolean = true;
  itemPriceQuantityPercent: number = 100;
  showAlert: boolean = false;
  alertMessage: string = '';
  projectCodeHint: string = 'Please enter the exact Project Code and hit “Enter” E.g.20170099, 20140023B01, 20200255F01';

  constructor(
    private store: Store<{ form: MyPurchaseForm; dp: StoreDropdowns, user: UserInformation }>,
    private route: ActivatedRoute, private sapAPI: SAPAPI,
    private backendService: BackendApiService
  ) {
    // set the mode for this component
    this.mode = this.route.snapshot.params['itemNo'] ? 'edit' : 'add';
    if (this.mode === 'edit') {
      this.itemNo = this.route.snapshot.params['itemNo'];
    }

    // static data for dropdown options
    this.budgetTypedropDownOptions = [
      { id: 'Operating', value: 'Operating' },
      { id: 'Capital', value: 'Capital'},
      { id: 'Capital AUC', value: 'Capital AUC'},
      { id: 'Project', value: 'Project' },
    ];
    this.budgetExpenditureOptions = [
      { id: true, value: 'Budgeted' },
      { id: false, value: 'Unbudgeted', disabled: true },
    ];

    this.budgetFrequencyOptions = [
      { id: true, value: 'Recurrent' },
      { id: false, value: 'Non-recurrent' },
    ];

    // store
    this.store.select('user').pipe(takeUntil(this.destroy$)).subscribe((user: UserInformation) => {
      this.userInformation = user;
    })
    this.store
      .select('dp')
      .pipe(takeUntil(this.destroy$))
      .subscribe((dp: StoreDropdowns) => {
        this.fundCentreDropDownOptions = dp.FundCentre;
        this.costCentreDropDownOptions = dp.CostCentre;
        this.originalProjectBudgetDropDownOptions = dp.ProjectBudget;
        this.originalGLAccountDropDownOptions = dp.GLAcc as ExtendedGLAccount[];
      });
  }
  async ngOnInit() {
    // Filter by operating by default
    if (this.userInformation && this.userInformation.userName) {
      let projectCodeResp = await lastValueFrom(this.backendService.getProjectCode(this.userInformation?.userName))
      const projectCodeOptions: any = [];
      projectCodeResp.forEach((option: any) => {
        let ddOption = {
          ...option,
          id: option.project,
          value: option.ProjectName,
        };
        projectCodeOptions.push(ddOption);
      });
      this.originalProjectCodeDropDownOptions = projectCodeOptions
      this.projectCodeDropDownOptions = this.originalProjectCodeDropDownOptions.filter(x => { let y = x as any; return y.IsVisible })
      const finalProjectBudgetDropDownOptions: any[] = []
      //loop through project code drop down options and whitelist those project budgets that have the project codes that is visible to ro
      for (const code of this.projectCodeDropDownOptions) {
        finalProjectBudgetDropDownOptions.push(this.originalProjectBudgetDropDownOptions.filter(
          (budget) => budget.ProjectCode == code.id
        ));
      }
      this.allowableProjectBudgetDropDownOptions = finalProjectBudgetDropDownOptions
    }
    this.filterGLAccountAndProjectByBudgetType('operating');
    // controls
    this.budgetTypeControl = this.control.controls['BudgetType'] as FormControl;
    this.budgetExpenditureControl = this.control.controls[
      'BudgetExpenditureUsage'
    ] as FormControl;
    this.budgetFrequencyControl = this.control.controls[
      'BudgetFrequencyUsage'
    ] as FormControl;

    // preselect when value of budgetExpenditureControl is null
    // to find better way of setting default value
    this.budgetExpenditureControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        if(!value) {
          this.budgetExpenditureControl.setValue(this.budgetExpenditureOptions.find((budget) => budget.id == true));
        }
      });

    // From Input
    this.budgetTypeControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.filterGLAccountAndProjectByBudgetType(
          value?.id.trim().toLowerCase()
        );
        this.showProjectCompletionMessage.emit(false)
        if (this.formType !== 'multi') this.showProjectCompletionMessage.emit(false)
        else this.shouldShowMultiBudgetFormProjectCompletionMessage = false
      });

    this.store
      .select('form')
      .pipe(takeUntil(this.destroy$))
      .subscribe((form: MyPurchaseForm) => {
        if (
          this.mode == 'edit' &&
          form.ItemList.length !== 0 &&
          this.formType == 'single'
        ) {
          this.isSingleBudget =
            form.ItemList[this.itemNo - 1].AddItems.isSingleBudget;
          let budgetData = form.ItemList[this.itemNo - 1].BudgetAllocation;

          console.log('budget data here', budgetData);
          let singleBudget = budgetData.find(x => !x.isDeleted && !x.Strikethrough)
          if (this.isSingleBudget && budgetData.length && singleBudget) {
            this.budgetTypeValue.emit(singleBudget.BudgetType.value)
            const {...remainingBudgetData } = singleBudget;
            if(singleBudget.ProjectCode?.id){
              const finalProjectBudgetDropDownOptions: any[] = []
              finalProjectBudgetDropDownOptions.push(this.originalProjectBudgetDropDownOptions.filter(
                (budget) => budget.ProjectCode == singleBudget?.ProjectCode?.id && budget.BudgetType === 8
              ));
              this.allowableProjectBudgetDropDownOptions = finalProjectBudgetDropDownOptions[0]
            }
            this.control.setValue({
              ...remainingBudgetData,
              BudgetExpenditureUsage: remainingBudgetData.BudgetExpenditureUsage
                ? this.budgetExpenditureOptions.find(
                  (option) =>
                    option.value ==
                    remainingBudgetData.BudgetExpenditureUsage?.value
                ) ?? null
                : null,
              BudgetFrequencyUsage: remainingBudgetData.BudgetFrequencyUsage
                ? this.budgetFrequencyOptions.find(
                  (option) =>
                    option.value == remainingBudgetData.BudgetFrequencyUsage?.value
                ) ?? null
                : null,
              PurchaseItemNo: this.itemNo,
              displayPurchaseItemNumber: form.ItemList.find(item => item.AddItems.itemId === this.itemNo)?.AddItems.displayItemNumber ?? this.itemNo
            });
            this.filterGLAccountAndProjectByBudgetType(
              budgetData[0].BudgetType.value.trim().toLowerCase()
            );
            if(singleBudget.ProjectCode?.id){
              this.glAccountDropDownOptions = this.originalGLAccountDropDownOptions.filter(
                (obj) => obj.BudgetTypes == 8 || obj.BudgetTypes === 12
              );
            }
          }
        }
      });

    if (this.BudgetForm) {
      this.currenBudgetList = this.BudgetForm.budgetList;
    }
    this.control.controls['BudgetExpenditureUsage'].valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((dropdown) => {
        if (dropdown && dropdown.value == 'Unbudgeted')
          this.control.controls['BudgetFrequencyUsage'].setValue(null)
      });
    this.control.controls['Percentage'].valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        if(value && !isNaN(value)) {
          // check if its multi and percentage, then proceed to computation
          // else, set to null
          if(this.formType == "multi" && !this.isQuantity) {
            if (value > this.calculatePercentageLeft()) {
              let val = this.calculatePercentageLeft();
              this.control.controls['Percentage'].setValue(
                val
              );
              return;
            }
            if (value < 0) {
              this.control.controls['Percentage'].setValue(0);
              return;
            }
            if (
              value.toString().split('.')[1]?.length > 2
            ) {
              this.control.controls['Percentage'].setValue(value);
              return;
            }
          } else {
            this.control.controls['Percentage'].setValue(null);
          }
        }
      });

    this.control.controls['Quantity'].valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        if (value && !isNaN(value) && value > this.calculateQuantityLeft()) {
          this.control.controls['Quantity'].setValue(
            this.calculateQuantityLeft()
          );
          return;
        }
        if (value && !isNaN(value) && value < 0) {
          this.control.controls['Quantity'].setValue(0);
          return;
        }
        if (
          value &&
          !isNaN(value) &&
          value.toString().split('.')[1]?.length > 2
        ) {
          this.control.controls['Quantity'].setValue(value);
          return;
        }
      });
  }

  handleBudgetTypeOptions() {
    // Reset the disabled state for all options
    this.budgetTypedropDownOptions = this.budgetTypedropDownOptions.map(option => {
      option.disabled = false;  // Reset disabled state to false for all options
      return option;
    });
    // Check if any operating budget type is present and not deleted or strikethrough
    const hasOperatingBudget = this.BudgetForm.budgetList.some(budget =>
      budget.BudgetType.value.toLowerCase() === "operating" &&
      !budget.isDeleted && !budget.Strikethrough
    );

    // Check if any capital or capital AUC budget type is present and not deleted or strikethrough
    const hasCapitalBudget = this.BudgetForm.budgetList.some(budget =>
      (budget.BudgetType.value.toLowerCase() === "capital" || budget.BudgetType.value.toLowerCase() === "capital auc") &&
      !budget.isDeleted && !budget.Strikethrough
    );

    this.budgetTypedropDownOptions = this.budgetTypedropDownOptions.map(option => {
      if ((hasOperatingBudget || !this.isQuantity) && (option.id === 'Capital' || option.id === 'Capital AUC')) {
        option.disabled = true; // Disable Capital and Capital AUC if there is an Operating budget or Percentage toggle selected
      }

      if (hasCapitalBudget && option.id === 'Operating') {
        option.disabled = true; // Disable Operating if there is a Capital or Capital AUC budget
      }

      return option;  // Return the updated option object
    });

    // Return the updated dropdown options
    return this.budgetTypedropDownOptions;
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["isReadOnly"]) {
      if (changes["isReadOnly"].currentValue == true) {
        this.control.disable();
      }
      else {
        this.control.enable();
      }
    }
    if (changes["BudgetForm"] || changes["ActionType"]) {
      this.showAlert = false;
      this.alertMessage = '';
      this.handleBudgetTypeOptions();
    }
  }

  calculateQuantityLeft() {
    if (this.BudgetForm)
      return (
        this.BudgetForm.Quantity -
        this.BudgetForm.budgetList.reduce(
          (total, { Quantity, isDeleted, iBudgetId, Strikethrough }) =>
            !isDeleted && !Strikethrough &&
              !(this.control.controls['iBudgetId'].value == iBudgetId)
              ? total + (Quantity ?? 0)
              : total,
          0
        )
      );
    return 0;
  }
  calculatePercentageLeft() {
    if (this.BudgetForm)
      return (
        100 -
        this.BudgetForm.budgetList.reduce(
          (total, { Percentage, isDeleted, iBudgetId, Strikethrough }) =>
            !isDeleted && !Strikethrough &&
              !(this.control.controls['iBudgetId'].value == iBudgetId)
              ? total + (Percentage ?? 0)
              : total,
          0
        )
      );
    return 0;
  }

  onBudgetTypeChange(event: BudgetTypeDropDownOptions) {
    // make sure iBudgetId is up to date.
    let iBudgetId = 0;
    if (this.formType == "multi" && this.BudgetForm && this.BudgetForm.budgetList && this.BudgetForm.budgetList.length > 0) {
      const existingId = this.BudgetForm.budgetList.reduce((max, item) => {
        return item.iBudgetId > max ? item.iBudgetId : max;
      }, 0);

      iBudgetId = existingId + 1;
    }

    // reset the form
    let newValue = {
      ...this.budgetInitialState,
      // include iBudgetId on reset of form
      iBudgetId: iBudgetId,
      BudgetType: event ? this.budgetTypedropDownOptions.find(
        (bType) => bType.id == event.id
      ) : null,
    };
    this.control.reset(newValue);
    // filter gl account based on budget type
    this.filterGLAccountAndProjectByBudgetType(event?.id?.trim().toLowerCase());
    this.budgetTypeValue.emit(event?.value)
  }
  addDropdownOption(event: any) {
    // let parent: any = document.getElementById('projectCodeManualInput')
    // let inputElement: HTMLInputElement = parent.querySelector('.ng-select-container').querySelector('.ng-value-container').querySelector('.ng-input').querySelector(':scope> input');
    // let enteredValue = inputElement.value
    const enteredValue = (event.target as HTMLInputElement).value;

    if (event.key == 'Enter') {
      if (enteredValue.length > 0) {
        let ddoption = this.originalProjectCodeDropDownOptions.find(x => x.id.toUpperCase() == String(enteredValue).toUpperCase())

        if (ddoption) {
          this.control.controls['ProjectCode'].setValue(ddoption)
          if (!this.projectCodeDropDownOptions.find(x => x.id == enteredValue)) {
            let newOptions = structuredClone(this.projectCodeDropDownOptions)
            newOptions.push(ddoption)
            this.projectCodeDropDownOptions = newOptions
            event.target.blur()
            this.onProjectCodeChange(ddoption);
          }

          //filter budgets by project code
          this.projectBudgetDropDownOptions =
            this.originalProjectBudgetDropDownOptions.filter(
              (budget) => String(budget.ProjectCode).toUpperCase() == ddoption?.id?.toUpperCase()
            );
        } else {
          this.projectCodeHint = "No result found, please check the project code and try again. E.g.20170099, 20140023B01, 20200255F01";
        }
      }
    }

  }
  searchOriginalDropdowns(term: any, item: any) {
		term = term.toUpperCase();
    return item.project.toUpperCase().indexOf(term) !== -1
  }

  onCostCentreChange() {
    this.control.controls['GLAcc'].setValue(null);
    this.control.controls['OrderNo'].setValue(null);
  }
  onFundCentreChange(){
    this.control.controls['GLAcc'].setValue(null);
  }
  onGLAccountChange(budgetType: string) {
    switch (budgetType) {
      case 'operating':
        this.control.controls['OrderNo'].setValue(null);
        break;
      default:
        //do Nothing
        break;
    }
  }
  onProjectCodeChange(event: DropDownOptions) {
    // to retrieve data from api in the future.
    console.log('onProjectCodeChange', event)
    this.projectBudgetDropDownOptions = []
    this.glAccountDropDownOptions = []
    this.control.controls['ProjectBudget'].setValue(null)
    this.control.controls['GLAcc'].setValue(null)
    // filter project budget by project code
    if (event) {
      this.projectBudgetDropDownOptions =
        this.originalProjectBudgetDropDownOptions.filter(
          (budget) => budget.ProjectCode == event.id
        );
    }
    if (this.formType !== 'multi') this.showProjectCompletionMessage.emit(false)
    else this.shouldShowMultiBudgetFormProjectCompletionMessage = false
  }
  async onProjectBudgetChange(event: any) {
      this.control.controls['GLAcc'].setValue(null)
      this.glAccountDropDownOptions = []
      if (event) {
      //show GL account options
      this.glAccountDropDownOptions =
        this.originalGLAccountDropDownOptions.filter(
          (obj) => obj.BudgetTypes == 8 || obj.BudgetTypes == 12
        );
      let showCompletionMessage = false
      const record: any = await lastValueFrom(this.backendService.getProjectVoteInfo(event.ProjectCode))
      if (record.hashLastMinuteProjectEqupmentPurchase) showCompletionMessage = true
      if (this.formType !== 'multi') this.showProjectCompletionMessage.emit(showCompletionMessage)
      else this.shouldShowMultiBudgetFormProjectCompletionMessage = showCompletionMessage
    }

  }


  filterGLAccountAndProjectByBudgetType(budgetType: string) {
    switch (budgetType) {
      case 'operating':
        this.glAccountDropDownOptions =
          this.originalGLAccountDropDownOptions.filter(
            (obj) => obj.BudgetTypes == 1
          );
        break;
      case 'capital':
        this.glAccountDropDownOptions =
          this.originalGLAccountDropDownOptions.filter(
            (obj) => obj.BudgetTypes == 2
          );
        break;
      case 'capital auc':
        this.projectBudgetDropDownOptions =
          this.originalProjectBudgetDropDownOptions.filter(
            (obj) => obj.BudgetType == 4
          );
        this.glAccountDropDownOptions =
          this.originalGLAccountDropDownOptions.filter(
            (obj) => obj.BudgetTypes == 4 || obj.BudgetTypes == 12
          );
        break;
      case 'project':
        //gl account drop down options will only be populated upon project budget selection
        this.glAccountDropDownOptions = []
        this.projectBudgetDropDownOptions =
          this.allowableProjectBudgetDropDownOptions.filter(
            (obj) => obj.BudgetType == 8
          );
        break;
      default:
        this.glAccountDropDownOptions =
          this.originalGLAccountDropDownOptions.filter(
            (obj) => obj.BudgetTypes == 1
          );
        break;
    }
  }
  getDuplicateIndex(entry: IBudgetAllocForm, excludeId?: number): number {
    type IBudgetAllocFormKey = keyof IBudgetAllocForm;
    const budgetType = entry.BudgetType?.id;
    const duplicateCheckFields: Record<string, IBudgetAllocFormKey[]> = {
      'Operating': ['CostCentre', 'GLAcc', 'BudgetFrequencyUsage'],
      'Capital': ['FundCentre', 'GLAcc'],
      'Capital AUC': ['ProjectBudget', 'GLAcc'],
      'Project': ['ProjectCode', 'ProjectBudget', 'GLAcc']
    };

    const fieldsToCheck = duplicateCheckFields[budgetType] || [];

    return this.BudgetForm.budgetList.findIndex(item =>
      item.Strikethrough !== true &&
      item.isDeleted !== true &&
      item.iBudgetId !== excludeId &&
      fieldsToCheck.every((field) => {
        const a = item[field] as any;
        const b = entry[field] as any;
        return a?.id === b?.id;
      })
    );
  };

  addBudget() {
    const entry: IBudgetAllocForm = { ...this.control.value };
    if (this.isQuantity && this.calculateQuantityLeft() <= 0) {

      this.alertMessage = 'Unable to add budget as quantity for this item has been used up.';
      this.showAlert = true;
      return;
    } else if (!this.isQuantity && this.calculatePercentageLeft() <= 0) {
      this.alertMessage = 'Unable to add budget as percentage for this item has been used up.';
      this.showAlert = true;
      return;
    }
    if (!this.isQuantity && !this.control.value.Percentage) {
      this.alertMessage = 'Please specify the split percentage for this budget.';
      this.showAlert = true;
      return;
    } else if (this.isQuantity && !this.control.value.Quantity) {
      this.alertMessage = 'Please specify the split quantity for this budget';
      this.showAlert = true;
      return;
    }
    if (this.ActionType === 'Add') {
      const duplicateIndex = this.getDuplicateIndex(entry);

      if (duplicateIndex === -1) {
        this.BudgetForm.budgetList.push({
          ...entry,
          iBudgetId: this.BudgetForm.budgetList.length + 1
        });
      } else {
        const existing = this.BudgetForm.budgetList[duplicateIndex];
        this.BudgetForm.budgetList[duplicateIndex] = {
          ...existing,
          Quantity: (existing.Quantity || 0) + (entry.Quantity || 0)
        };
      }

    } else if (this.ActionType === 'Edit') {
      const duplicateIndex = this.getDuplicateIndex(entry, entry.iBudgetId);

      if (duplicateIndex !== -1) {
        const existing = this.BudgetForm.budgetList[duplicateIndex];
        this.BudgetForm.budgetList[duplicateIndex] = {
          ...existing,
          Quantity: (existing.Quantity || 0) + (entry.Quantity || 0)
        };

        const originalIndex = this.BudgetForm.budgetList.findIndex(
          (item) => item.iBudgetId === entry.iBudgetId
        );
        if (originalIndex !== -1 && originalIndex !== duplicateIndex) {
          this.BudgetForm.budgetList.splice(originalIndex, 1);
        }

      } else {
        const index = this.BudgetForm.budgetList.findIndex(
          (item) => item.iBudgetId === entry.iBudgetId
        );
        if (index !== -1) {
          this.BudgetForm.budgetList[index] = { ...entry };
        }
      }
    }
    this.budgetTypeValue.emit(this.control.value.BudgetType.value)
    this.addBudgetHandler.emit(this.BudgetForm);
  }

  setControlValueFromRow(event: BudgetTableAction) {
    // multi Budget
    if (event.row) {
      this.control.setValue({
        iBudgetId: event.row.iBudgetId,
        BudgetItemId: event.row.BudgetItemId,
        Quantity: event.row.Quantity,
        BudgetType: event.row.BudgetType,
        CostCentre: event.row.CostCentre,
        GLAcc: event.row.GLAcc,
        OrderNo: event.row.OrderNo,
        Percentage: event.row.Percentage,
        FundCentre: event.row.FundCentre,
        ProjectBudget: event.row.ProjectBudget,
        BudgetExpenditureUsage:
          this.budgetExpenditureOptions.find(
            (option) => option.value == event.row?.BudgetExpenditureUsage?.value
          ) ?? null,
        BudgetFrequencyUsage:
          this.budgetFrequencyOptions.find(
            (option) => option.value == event.row?.BudgetFrequencyUsage?.value
          ) ?? null,
        ProjectCode: event.row.ProjectCode,
        PurchaseItemNo: event.row.PurchaseItemNo,
        displayPurchaseItemNumber: event.row.displayPurchaseItemNumber,
        ApprovingOfficer: event.row.ApprovingOfficer,
        isDeleted: event.row.isDeleted,
        Strikethrough: event.row.Strikethrough
      });
    } else {
      this.control.setValue({
        ...this.budgetInitialState,
        BudgetType: { id: event.BudgetType, value: event.BudgetType },
        iBudgetId: this.BudgetForm.budgetList.length + 1,
      });
    }
  }

  onQuantityKeyPress(event: KeyboardEvent): void {
    const inputChar = event.key;
    // Allow only digits and prevent decimal point or negative sign
    if (inputChar === '.' || inputChar === '-' || inputChar === 'e' || inputChar === 'E') {
      event.preventDefault();
    }
  }

  public get alertType(): typeof AlertType {
    return AlertType;
  }
}
